/**
 * The undo router: pick a source, then execute it.
 *
 * Ordered by cost, cheapest first. Every step is a *hard* gate — a source is only
 * chosen when it is actually usable, and a rejected source falls through to the
 * next rather than failing the undo:
 *
 *   1. LIVE COMMAND      the object the tool stashed at apply time. Free and
 *                        exact, but dies on reload.
 *   2. LOCAL SNAPSHOT    the pre-apply socket in this device's memory or its
 *                        IndexedDB scene cache. Free, exact, same-device only.
 *   3. RECIPE            re-run the tool from its parameters. Requires a verified
 *                        deterministic handler, intact parameters, *and* a local
 *                        anchor mesh — see the note on routing below.
 *   4. SERVER BLOB       download the stored pre-apply socket. Always correct.
 *   5. PREV CHECKPOINT   the previous step's finished mesh. Costs nothing extra to
 *                        store, and is the only source left after export pruning.
 *   6. none              the button says so instead of pretending.
 *
 * ── On routing recipe vs download ───────────────────────────────────────────
 * A replay needs a mesh to replay *onto*. On a freshly-opened device the only
 * mesh in memory is the finished one, so replay would have to download an anchor
 * first — a transfer of the same order as simply downloading the stored
 * pre-apply socket, and then pay the recompute on top. Replay therefore only wins
 * when its anchor is **already local**, which is the primary condition below.
 * Recorded durations are the secondary check: even with a local anchor, a replay
 * predicted to take longer than the download loses.
 */
import useEditorStore from '../../../shared/stores/useEditorStore';
import { GLOBAL_STEP_ORDER } from '../../../shared/constants/flowConfig';
import { hasUndoSnapshot, adoptUndoSnapshot, restoreUndoSnapshot } from '../undoSnapshot';
import { hasMeshForStep, restoreMeshForStep } from '../meshSnapshot';
import useOperationLogStore from '../operationLog/operationLogStore';
import { getOperationHandler } from '../operationLog/operationRegistry';
import useUndoManifestStore, { BEFORE_KIND } from './undoManifestStore';
import { canUseRecipe, describeStrategy } from './undoStrategy';
import {
  hasServerBeforeMesh,
  loadBeforeMeshPayload,
  hydrateUndoManifests,
} from './undoManifestSync';
import { hasLocalBeforeMesh } from './beforeMeshCache';
import { restorePreviousCheckpointSocket } from './prevStepCheckpoint';
import {
  predictDownloadMs,
  scaleRecordedDuration,
  REPLAY_OVERRUN_FACTOR,
} from './transferRate';
import { loadBkDesignOrderContext } from '../../../shared/lib/bkDesignOrderContext';

const LOG_PREFIX = '[UndoRouter]';

export const UNDO_SOURCE = {
  COMMAND: 'command',
  LOCAL_SNAPSHOT: 'local_snapshot',
  RECIPE: 'recipe',
  SERVER_BLOB: 'server_blob',
  PREV_CHECKPOINT: 'prev_checkpoint',
  NONE: 'none',
};

/** Typical socket blob when the manifest did not record a size. */
const ASSUMED_BLOB_BYTES = 6 * 1024 * 1024;

/**
 * Tools whose vertices carry a baked world frame the compare overlay must follow.
 * Every caller that restores a pre-apply socket has to pass this through, so it
 * lives with the restore chain rather than being repeated per call site.
 */
export const REFRAME_COMPARE_BASELINE_TOOLS = new Set(['main_locking_lock']);

function orderKey() {
  const ctx = loadBkDesignOrderContext();
  return ctx ? `${ctx.sales_order}:${ctx.customer}` : null;
}

/** Step immediately before `stepId` in the global flow. */
function previousStepId(stepId) {
  const idx = GLOBAL_STEP_ORDER.indexOf(stepId);
  return idx > 0 ? GLOBAL_STEP_ORDER[idx - 1] : null;
}

/**
 * Index of the op to stop before when replaying a step without `toolId`: the
 * position of that tool's most recent apply.
 */
function replayCutIndex(ops, toolId) {
  for (let i = ops.length - 1; i >= 0; i--) {
    if (ops[i].toolId === toolId) return i;
  }
  return -1;
}

/**
 * Is there a local mesh to replay on top of, i.e. the socket as it stood at the
 * start of this step?
 */
function hasLocalReplayAnchor(stepId) {
  const prev = previousStepId(stepId);
  return !!prev && hasMeshForStep(prev);
}

/**
 * Predicted cost of replaying this step's ops up to (excluding) `toolId`, in ms
 * on *this* device.
 */
function predictReplayMs(stepId, toolId) {
  const entries = useUndoManifestStore.getState().getEntriesForStep(stepId);
  const cut = replayCutIndex(entries, toolId);
  const kept = cut < 0 ? entries : entries.slice(0, cut);
  return kept.reduce(
    (sum, e) => sum + scaleRecordedDuration(e.durationMs, e.deviceVerticesPerMs),
    0,
  );
}

/**
 * Decide where an undo of `toolId` on `stepId` should come from.
 *
 * Synchronous, so it can drive the Undo button's enabled state during render.
 * It answers from recorded state only — it never touches the network. The server
 * blob branch relies on {@link hydrateUndoManifests} having run for the order,
 * which happens when the design is opened.
 *
 * @param {{ undo?: () => void } | null | undefined} command
 * @param {string} toolId
 * @param {string} stepId
 * @returns {{ source: string, reason?: string, detail?: object }}
 */
export function resolveUndoSource(command, toolId, stepId) {
  if (command) return { source: UNDO_SOURCE.COMMAND };
  if (!toolId) return { source: UNDO_SOURCE.NONE, reason: 'no_tool_id' };

  if (hasUndoSnapshot(toolId)) return { source: UNDO_SOURCE.LOCAL_SNAPSHOT };

  const manifest = useUndoManifestStore.getState();
  const ref = stepId ? manifest.getBeforeRef(stepId, toolId) : null;
  const key = orderKey();
  const localBlob = !!(key && stepId && hasLocalBeforeMesh(key, stepId, toolId));
  const serverBlob = !!(stepId && hasServerBeforeMesh(stepId, toolId));

  // Recipe: only with a local anchor, and only when it is predicted to be faster
  // than the download it is competing with.
  if (stepId && !manifest.hasToolListDrift(stepId)) {
    const ops = useOperationLogStore.getState().getOperationsForStep(stepId);
    const op = ops.find((o) => o.toolId === toolId) ?? null;
    const gate = canUseRecipe(toolId, op);
    if (gate.ok && hasLocalReplayAnchor(stepId)) {
      const replayMs = predictReplayMs(stepId, toolId);
      const downloadMs = predictDownloadMs(ref?.bytes ?? ASSUMED_BLOB_BYTES, {
        alreadyLocal: localBlob,
      });
      if (replayMs <= downloadMs) {
        return {
          source: UNDO_SOURCE.RECIPE,
          detail: { replayMs, downloadMs, budgetMs: replayMs * REPLAY_OVERRUN_FACTOR },
        };
      }
    }
  }

  if (ref?.kind === BEFORE_KIND.BLOB && (localBlob || serverBlob)) {
    return { source: UNDO_SOURCE.SERVER_BLOB, detail: { local: localBlob } };
  }
  if (localBlob || serverBlob) {
    // A blob exists that this session has no manifest entry for — still usable.
    return { source: UNDO_SOURCE.SERVER_BLOB, detail: { local: localBlob, unrecorded: true } };
  }

  // No blob was ever needed for the step's first tool: the previous step's
  // finished checkpoint is the pre-apply socket.
  if (ref?.kind === BEFORE_KIND.PREV_STEP_CHECKPOINT) {
    return { source: UNDO_SOURCE.PREV_CHECKPOINT };
  }
  if (stepId && manifest.getStepPointers(stepId).firstToolId === toolId) {
    return { source: UNDO_SOURCE.PREV_CHECKPOINT };
  }
  // Nothing recorded at all (manifest lost, or applied by an older build) but the
  // order does have earlier checkpoints — offer the coarse path rather than
  // disabling the button outright.
  if (stepId && previousStepId(stepId) && key) {
    return { source: UNDO_SOURCE.PREV_CHECKPOINT, detail: { speculative: true } };
  }

  return { source: UNDO_SOURCE.NONE, reason: 'no_undo_material' };
}

/** True when an undo of this tool can be performed right now. */
export function canUndoFromAnySource(command, toolId, stepId) {
  return resolveUndoSource(command, toolId, stepId).source !== UNDO_SOURCE.NONE;
}

/**
 * Replay this step's operations up to, but excluding, `toolId`.
 *
 * Restores the start-of-step anchor first, so the result is "the socket as it was
 * immediately before that tool ran". Everything applied *after* it is dropped —
 * matching what the live-command path does via `resetHistoryUpTo`.
 *
 * @param {string} stepId
 * @param {string} toolId
 * @param {{ signal?: AbortSignal, budgetMs?: number }} [opts]
 * @returns {Promise<boolean>}
 */
export async function replayStepExcludingTool(stepId, toolId, { signal, budgetMs } = {}) {
  const anchor = previousStepId(stepId);
  if (!anchor || !hasMeshForStep(anchor)) return false;
  if (!restoreMeshForStep(anchor)) return false;

  const ops = useOperationLogStore.getState().getOperationsForStep(stepId);
  const cut = replayCutIndex(ops, toolId);
  const toReplay = cut < 0 ? ops : ops.slice(0, cut);

  const started = performance.now();
  for (const op of toReplay) {
    if (signal?.aborted) return false;
    if (Number.isFinite(budgetMs) && performance.now() - started > budgetMs) {
      console.warn(
        `${LOG_PREFIX} replay of ${stepId} overran its ${Math.round(budgetMs)}ms budget — ` +
          'abandoning for the download path',
      );
      return false;
    }

    const handler = getOperationHandler(op.toolId);
    if (!handler) {
      console.warn(`${LOG_PREFIX} no handler for ${op.toolId} mid-replay`);
      return false;
    }

    const editor = useEditorStore.getState();
    const mesh = editor.sceneRefs?.shadedMesh;
    if (!mesh) return false;

    const next = await handler.replay(
      { mesh, scene: editor.sceneRefs?.scene, signal },
      op.params,
    );
    if (next && next !== mesh) useEditorStore.getState().replaceShadedMesh(next);
    else useEditorStore.getState().incrementGeometryVersion();
  }
  return true;
}

/**
 * Carry out the plan `resolveUndoSource` produced.
 *
 * Falls through on failure exactly as the resolver would have: a recipe that
 * overruns, a blob that 404s or a checkpoint that will not install all hand over
 * to the next source rather than leaving the socket half-undone.
 *
 * @param {{ source: string, detail?: object }} plan
 * @param {string} toolId
 * @param {string} stepId
 * @param {{ signal?: AbortSignal, reframeCompareBaseline?: boolean }} [opts]
 * @returns {Promise<{ ok: boolean, source: string }>}
 */
export async function executeUndoSource(plan, toolId, stepId, opts = {}) {
  const { signal, reframeCompareBaseline = false } = opts;
  const order = orderKey();

  if (plan.source === UNDO_SOURCE.LOCAL_SNAPSHOT) {
    const ok = restoreUndoSnapshot(toolId, { reframeCompareBaseline });
    if (ok) return { ok: true, source: UNDO_SOURCE.LOCAL_SNAPSHOT };
  }

  if (plan.source === UNDO_SOURCE.RECIPE) {
    const ok = await replayStepExcludingTool(stepId, toolId, {
      signal,
      budgetMs: plan.detail?.budgetMs,
    });
    if (ok) return { ok: true, source: UNDO_SOURCE.RECIPE };
    console.warn(`${LOG_PREFIX} recipe path failed for ${toolId} — trying stored socket`);
  }

  if (
    plan.source === UNDO_SOURCE.SERVER_BLOB ||
    plan.source === UNDO_SOURCE.RECIPE
  ) {
    if (order) {
      const payload = await loadBeforeMeshPayload(stepId, toolId, { signal });
      if (payload && adoptUndoSnapshot(toolId, payload)) {
        const ok = restoreUndoSnapshot(toolId, { reframeCompareBaseline });
        if (ok) return { ok: true, source: UNDO_SOURCE.SERVER_BLOB };
      }
      console.warn(`${LOG_PREFIX} stored socket unavailable for ${stepId}/${toolId}`);
    }
  }

  // Last resort for every failed path above, not just an explicit plan.
  const { ok } = await restorePreviousCheckpointSocket(stepId, { signal });
  if (ok) return { ok: true, source: UNDO_SOURCE.PREV_CHECKPOINT };

  return { ok: false, source: UNDO_SOURCE.NONE };
}

/** Human-readable reason for a disabled Undo button. */
export function undoUnavailableReason(toolId, stepId) {
  const plan = resolveUndoSource(null, toolId, stepId);
  if (plan.source !== UNDO_SOURCE.NONE) return null;
  if (!orderKey()) {
    return 'Undo is unavailable for a design that was not opened from an order.';
  }
  return 'Undo is unavailable here — this tool was applied before undo history was saved for this order.';
}

/** Console diagnostics for a support ticket. */
export function explainUndoSource(command, toolId, stepId) {
  const plan = resolveUndoSource(command, toolId, stepId);
  const ops = stepId
    ? useOperationLogStore.getState().getOperationsForStep(stepId)
    : [];
  return {
    toolId,
    stepId,
    chosen: plan.source,
    reason: plan.reason ?? null,
    detail: plan.detail ?? null,
    declared: describeStrategy(toolId, ops.find((o) => o.toolId === toolId) ?? null),
    localAnchor: stepId ? hasLocalReplayAnchor(stepId) : false,
  };
}
